import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const arkGalleryStates = new WeakMap();
const arkKey = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function arkGalleryConfig(gallery) {
  try { return JSON.parse(gallery.querySelector('[data-ark-gallery-data]')?.textContent || 'null'); }
  catch { return null; }
}

function arkGalleryChoices(picker, config) {
  return config.options.map((name) => {
    const values = [...picker.querySelectorAll('input:checked, select option:checked')];
    const selected = values.find((input) => input.dataset.optionName === name);
    if (selected) return selected.value;
    const option = config.values[config.options.indexOf(name)];
    return option.length === 1 ? option[0].name : '';
  });
}

function arkGallerySelection(state, picker) {
  const config = state.config;
  const choices = arkGalleryChoices(picker, config);
  const variant = config.variants.find((v) => v.options.every((value, i) => value === choices[i]));
  const groupIndex = config.options.findIndex((name) => /^(colou?r|pattern|style|design)$/i.test(name));
  let media = variant?.media;
  if (!media && groupIndex >= 0) {
    const sameGroup = config.variants.find((v) => v.media && v.options[groupIndex] === choices[groupIndex]);
    media = sameGroup?.media;
  }
  if (!media) {
    for (let i = 0; i < choices.length; i++) {
      if (choices[i] !== state.choices?.[i]) {
        media = config.values[i].find((v) => v.name === choices[i])?.media;
        if (media) break;
      }
    }
  }
  return { media: media ? String(media) : null, choices, groupIndex };
}

function arkGalleryWanted(state, selection) {
  const { config } = state;
  const selected = selection.media;
  if (!selected || !config.media.some((m) => String(m.id) === selected)) return null;
  const index = selection.groupIndex;
  const groups = new Map();
  for (const variant of config.variants) {
    if (!variant.media) continue;
    const key = String(variant.media);
    const group = index >= 0 ? variant.options[index] : key;
    if (!groups.has(key)) groups.set(key, new Set());
    groups.get(key).add(group);
  }
  const selectedGroup = index >= 0 ? selection.choices[index] : selected;
  const groupValues = index >= 0 ? config.values[index] : [];
  const assigned = config.media.filter((item) => groups.has(String(item.id)));
  for (const item of config.media) {
    const file = new URL(item.src, location.href).pathname.split('/').pop().toLowerCase();
    if (!groups.has(String(item.id))) {
      const matches = assigned.filter((main) => {
        const name = new URL(main.src, location.href).pathname.split('/').pop().toLowerCase().replace(/\.[^.]+$/, '');
        return file.startsWith(name) && /^[\d._-]/.test(file.slice(name.length));
      });
      if (matches.length === 1) groups.set(String(item.id), groups.get(String(matches[0].id)));
    }
    for (const value of groupValues) {
      const tag = `--${arkKey(config.options[index])}-${arkKey(value.name)}`;
      if (file.includes(tag + '.') || file.includes(tag + '--')) groups.set(String(item.id), new Set([value.name]));
      if (config.productId === 7135983894577 && file.startsWith(`ark-grip-socks-${arkKey(value.name)}-`)) {
        groups.set(String(item.id), new Set([value.name]));
      }
    }
  }
  const wanted = config.media.filter((m) => !groups.has(String(m.id)) || groups.get(String(m.id)).has(selectedGroup)).map((m) => String(m.id));
  return [selected, ...wanted.filter((id) => id !== selected)];
}

function arkGalleryBuild(state, ids) {
  const gallery = state.source.cloneNode(true);
  const originals = state.config.media.map((m) => String(m.id));
  const reorder = (selector, wrap = false) => {
    const nodes = [...gallery.querySelectorAll(selector)];
    if (!nodes.length) return;
    const units = nodes.map((n) => wrap ? n.parentElement : n);
    const parent = units[0].parentElement;
    units.forEach((n) => n.remove());
    ids.forEach((id, index) => {
      const oldIndex = originals.indexOf(id);
      const unit = units[oldIndex];
      if (!unit) return;
      for (const node of [unit, ...unit.querySelectorAll('*')]) {
        for (const attr of [...node.attributes]) {
          let value = attr.value;
          if (attr.name.startsWith('on:')) value = value.replace(/\/(open|select|zoom|preloadImage|handleThumbnailClick|handleThumbnailPointerEnter)\/\d+/g, `/$1/${index}`);
          if (attr.name === 'style') value = value.replace(/--slide-\d+/g, `--slide-${index}`);
          if (value !== attr.value) node.setAttribute(attr.name, value);
        }
      }
      if (unit.matches('slideshow-slide')) unit.setAttribute('aria-hidden', String(index !== 0));
      const button = unit.matches('button') ? unit : unit.querySelector('button[ref="dots[]"]');
      if (button) {
        button.toggleAttribute('aria-selected', index === 0);
        if (index === 0) button.setAttribute('aria-selected', 'true');
        if (['thumbnails[]', 'dots[]'].includes(button.getAttribute('ref')) && /\d/.test(button.getAttribute('aria-label') || '')) {
          button.setAttribute('aria-label', button.getAttribute('aria-label').replace(/\d+/, String(index + 1)).replace(/\d+$/, String(ids.length)));
        }
        if (button.getAttribute('ref') === 'dots[]') button.textContent = String(index + 1);
      }
      parent.append(unit);
    });
  };
  reorder('slideshow-slide');
  for (const controls of [...gallery.querySelectorAll('slideshow-controls')]) {
    const marker = `ark-controls-${Math.random().toString(36).slice(2)}`;
    controls.setAttribute('data-ark-controls', marker);
    reorder(`[data-ark-controls="${marker}"] [ref="thumbnails[]"]`);
    reorder(`[data-ark-controls="${marker}"] [ref="dots[]"]`, true);
    controls.removeAttribute('data-ark-controls');
    const counter = controls.querySelector('.slideshow-controls__counter');
    if (counter?.lastChild?.nodeType === Node.TEXT_NODE) counter.lastChild.textContent = String(ids.length);
  }
  reorder('.media-gallery__grid > li');
  reorder('.dialog-thumbnails-list__thumbnail');
  reorder('.dialog-zoomed-gallery > li');
  const slideshow = gallery.querySelector('slideshow-component');
  if (slideshow) {
    slideshow.style.setProperty('--slideshow-timeline', ids.map((_, i) => `--slide-${i}`).join(', '));
    slideshow.classList.toggle('slideshow--single-media', ids.length === 1);
    slideshow.querySelectorAll('slideshow-controls, slideshow-arrows').forEach((node) => { node.hidden = ids.length === 1; });
  }
  const image = gallery.querySelector('slideshow-slide img');
  if (image) { image.loading = 'eager'; image.fetchPriority = 'high'; }
  gallery.dataset.arkGalleryReady = 'true';
  gallery.dataset.arkVisibleMedia = ids.join(',');
  return gallery;
}

function arkGalleryWarm(state) {
  const connection = navigator.connection;
  if (connection?.saveData || /^(slow-)?2g$/.test(connection?.effectiveType || '')) return;
  const run = async () => {
    const ids = [...new Set(state.config.variants.map((v) => v.media).filter(Boolean))].slice(0, 8);
    for (const id of ids) {
      if (!state.section.isConnected) break;
      const at = state.config.media.findIndex((m) => m.id === id);
      const source = state.source.querySelectorAll('slideshow-slide')[at]?.querySelector('img');
      if (!source) continue;
      const image = new Image();
      image.fetchPriority = 'low'; image.decoding = 'async';
      image.sizes = source.sizes; image.srcset = source.srcset; image.src = source.src;
      await Promise.race([image.decode().catch(() => {}), new Promise((r) => setTimeout(r, 3000))]);
    }
  };
  const schedule = () => window.requestIdleCallback ? window.requestIdleCallback(run) : setTimeout(run, 1000);
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
}

function arkGalleryInit(gallery) {
  const section = gallery.closest('.shopify-section');
  if (!section || gallery.closest('dialog, product-card')) return null;
  const config = arkGalleryConfig(gallery);
  if (!config?.variants || config.variants.length < 2 || config.media.length < 2) return null;
  let state = arkGalleryStates.get(section);
  if (state?.config.productId === config.productId) return state;
  const picker = section.querySelector(`variant-picker[data-product-id="${config.productId}"]`);
  if (!picker || gallery.querySelectorAll('slideshow-slide').length !== config.media.length) return null;
  state = { section, config, source: gallery.cloneNode(true), sequence: 0, choices: null };
  arkGalleryStates.set(section, state);
  const selection = arkGallerySelection(state, picker);
  state.choices = selection.choices;
  const ids = arkGalleryWanted(state, { ...selection, media: String(config.selectedMedia || selection.media || '') });
  if (ids) queueMicrotask(() => { if (gallery.isConnected) gallery.replaceWith(arkGalleryBuild(state, ids)); });
  arkGalleryWarm(state);
  return state;
}

function arkGalleryPreview(gallery, event) {
  const picker = event.target;
  if (!(picker instanceof Element) || !picker.matches('variant-picker') || picker.closest('dialog, product-card')) return false;
  const state = arkGalleryInit(gallery);
  if (!state || picker.dataset.productId !== String(state.config.productId) || picker.closest('.shopify-section') !== state.section) return false;
  const linked = [...picker.querySelectorAll('input:checked, option:checked')].some((input) => input.dataset.connectedProductUrl && input.dataset.connectedProductUrl.split('?')[0] !== picker.dataset.productUrl.split('?')[0]);
  if (linked) return false;
  const selection = arkGallerySelection(state, picker);
  const ids = arkGalleryWanted(state, selection);
  state.choices = selection.choices;
  const sequence = ++state.sequence;
  if (ids && gallery.dataset.arkVisibleMedia !== ids.join(',')) gallery.replaceWith(arkGalleryBuild(state, ids));
  event.promise.then(({ detail }) => {
    if (sequence !== state.sequence || !state.section.isConnected) return;
    const authoritative = detail?.html?.querySelector(`media-gallery[data-ark-product-id="${state.config.productId}"]`);
    const config = authoritative && arkGalleryConfig(authoritative);
    if (!config) return;
    const current = state.section.querySelector(`media-gallery[data-ark-product-id="${state.config.productId}"]`);
    if (!current) return;
    state.config = config;
    state.source = authoritative.cloneNode(true);
    const updated = arkGallerySelection(state, state.section.querySelector('variant-picker'));
    const finalIds = arkGalleryWanted(state, { ...updated, media: config.selectedMedia ? String(config.selectedMedia) : updated.media });
    if (finalIds && current.dataset.arkVisibleMedia !== finalIds.join(',')) current.replaceWith(arkGalleryBuild(state, finalIds));
    else if (!finalIds) current.replaceWith(authoritative);
  }).catch((error) => { if (error?.name !== 'AbortError' && sequence === state.sequence) console.warn('[ARK gallery] Variant refresh failed.', error); });
  return true;
}


/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    arkGalleryInit(this);

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('ark:gallery-select', this.#handleArkGallerySelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #handleArkGallerySelect = (event) => {
    if (event.detail?.productId !== this.dataset.arkProductId) return;
    const config = arkGalleryConfig(this);
    if (!config) return;
    const section = this.closest('.shopify-section');
    let state = arkGalleryStates.get(section);
    if (!state || state.config.productId !== config.productId) {
      state = {section, config, source:this.cloneNode(true), sequence:0};
      arkGalleryStates.set(section,state);
    }
    const ids = event.detail.ids.filter((id) => state.config.media.some((m) => String(m.id) === id));
    if (!ids.length) return;
    this.replaceWith(arkGalleryBuild(state,ids));
  };

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (arkGalleryPreview(this, event)) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

